<script setup lang="ts">
import { Icon } from '@iconify/vue';
import { useDebounceFn } from '@vueuse/core';
import { computed } from 'vue';
import { IconLib } from '@/assets/icon-library.ts';

const props = defineProps<{
   modelValue?: string;
}>();

const emit = defineEmits<{
   'search': [query: string];
   'update:modelValue': [value: string];
}>();

const searchQuery = computed({
   get: () => props.modelValue || '',
   set: (value: string) => {
      emit('update:modelValue', value);
   },
});

/**
 * Debounced search for better UX (searches as user types)
 * Debounce time is 300ms
 */
const debouncedSearch = useDebounceFn(() => {
   const query = searchQuery.value;
   emit('update:modelValue', query);
   emit('search', query);
}, 300);

function handleClear() {
   searchQuery.value = '';
   emit('update:modelValue', '');
   emit('search', '');
}
</script>

<template>
   <div class="flex items-center gap-2 rounded-full bg-[#EFEDED] py-1 ps-4 pe-2">
      <input
         v-model="searchQuery"
         type="text"
         placeholder="Search chats..."
         aria-label="Search chats"
         class="flex-1 bg-transparent outline-none placeholder:text-black/70 font-medium min-w-0"
         @input="debouncedSearch"
         @keydown.escape="handleClear"
      >
      <button
         v-if="searchQuery"
         aria-label="Clear search"
         class="text-black/70 hover:bg-white rounded-full p-1 transition-colors duration-150"
         @click="handleClear"
      >
         <Icon :icon="IconLib.actions.close" class="size-5" />
      </button>
      <div
         v-else
         class="text-black/70 p-1"
      >
         <Icon :icon="IconLib.actions.search" class="size-5" />
      </div>
   </div>
</template>

<style scoped lang="postcss">

</style>
